import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db, functions } from '../../firebase';
import { collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import './AIChatButton.css';

const AIChatButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();

  // Subscribe to messages from Firestore
  useEffect(() => {
    if (!user) return;

    const messagesRef = collection(db, 'aiChats');
    const q = query(
      messagesRef,
      where('userId', '==', user.uid),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(newMessages);
    });

    return () => unsubscribe();
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const saveMessage = async (message, role) => {
    if (!user) return;

    await addDoc(collection(db, 'aiChats'), {
      userId: user.uid,
      content: message,
      role: role,
      timestamp: serverTimestamp()
    });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isLoading || !user) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setIsLoading(true);

    try {
      // Save user message to Firestore
      await saveMessage(messageText, 'user');

      // Call the Cloud Function
      console.log('Calling generateAIResponse with message:', messageText);
      const generateAIResponse = httpsCallable(functions, 'generateAIResponse');
      const result = await generateAIResponse({ message: messageText });
      console.log('Cloud function response:', result.data);

      // The assistant's response will be added to Firestore by the cloud function
      // and will appear automatically through the real-time listener
    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      if (error.code === 'functions/unauthenticated') {
        await saveMessage(
          "Sorry, you need to be authenticated to use the chat.",
          'assistant'
        );
      } else if (error.code === 'functions/internal') {
        await saveMessage(
          "I'm sorry, I'm having trouble connecting to the AI service. Please try again later.",
          'assistant'
        );
      } else {
        await saveMessage(
          "I'm sorry, something went wrong. Please try again.",
          'assistant'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="ai-chat-container">
      {isOpen && (
        <div className="ai-chat-window">
          <div className="ai-chat-header">
            <h3>AI Assistant</h3>
            <button className="close-button" onClick={toggleChat}>×</button>
          </div>
          <div className="ai-chat-messages">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`message-bubble ${message.role}`}
              >
                {message.content}
              </div>
            ))}
            {isLoading && (
              <div className="message-bubble assistant loading">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form className="ai-chat-input" onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder="Ask me anything..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading || !newMessage.trim()}>
              {isLoading ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      )}
      <button 
        className="ai-chat-button"
        onClick={toggleChat}
      >
        <span className="ai-icon">🤖</span>
        <span className="ai-text">AI Assistant</span>
      </button>
    </div>
  );
};

export default AIChatButton; 